import React, { useState, useEffect } from 'react';

import { Helmet } from 'react-helmet-async';

import './AdminProfile.css'; // Import your CSS styles
import { 
  User, Mail, Shield, Calendar, Edit3, Save, X, Eye, EyeOff, Lock, 
  CheckCircle, AlertCircle, Camera, Key, Bell, Globe, Smartphone, MapPin
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL ;

const AdminProfile = () => {
  const [adminProfile, setAdminProfile] = useState({
    id: '', username: '', email: '', role: 'admin', fullName: '', phone: '',
    address: '', timezone: '', language: 'en', emailNotifications: true,
    smsNotifications: false, loginAlerts: true, createdAt: '', lastLogin: '', loginCount: 0
  });

  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');


   const pageTitle = isEditing 
    ? `Editing Profile | GN VISA Admin` 
    : `${adminProfile.fullName || 'Admin'}'s Profile | GN VISA Admin`;
      
  const pageDescription = isEditing 
    ? `Update your personal information, security settings, and notification preferences for the GN VISA admin panel.`
    : `View your profile details, security settings, and account activity for the GN VISA admin panel.`;


  const fetchAdminProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('adminToken');
      if (!token) {
        setError('No authentication token found. Please log in again.');
        setLoading(false);
        return; 
      }
      const response = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to fetch profile. Status: ${response.status}`);
      }
      const data = await response.json();
      if (!data) throw new Error("Received empty profile data from the server.");
      setAdminProfile(data);
      setEditForm(data);
    } catch (error) {
      console.error('Error fetching admin profile:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdminProfile();
  }, []);

  const handleEditToggle = () => {
    if (isEditing) setEditForm(adminProfile);
    setIsEditing(!isEditing);
    setError(null);
    setSuccess(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

   const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('No authentication token found');

      // FIX: Prepend API_BASE_URL to the fetch call
      const response = await fetch(`${API_BASE_URL}/api/admin/profile`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(editForm)
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to update profile: ${response.status}`);
      }

      const updatedProfile = await response.json();
      setAdminProfile(updatedProfile);
      setEditForm(updatedProfile);
      setIsEditing(false);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.message);
    } finally {
      setSaving(false);
    }
  };


   const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

   const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setPasswordError('All password fields are required');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      if (!token) throw new Error('No authentication token found');

      // FIX: Prepend API_BASE_URL to the fetch call
      const response = await fetch(`${API_BASE_URL}/api/admin/change-password`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          currentPassword: passwordForm.currentPassword,
          newPassword: passwordForm.newPassword
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to change password');
      }

      setPasswordSuccess('Password changed successfully!');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setShowPasswordForm(false);
      setTimeout(() => setPasswordSuccess(''), 3000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.message);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not available';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };
  const getTimeAgo = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    if (diffInDays === 0) return 'Today';
    if (diffInDays === 1) return 'Yesterday';
    return formatDate(dateString);
  };


   if (loading) {
    // It's good practice to set a title for the loading state too
    return (
      <>
        <Helmet>
          <title>Loading Profile...</title>
        </Helmet>
        <div className="admin-profile-loading">
          <div className="loading-spinner"></div><p>Loading profile...</p>
        </div>
      </>

    )};

  return (

    
    <div className="admin-profile-container">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
      </Helmet>

      <div className="admin-profile-header">
        <div className="profile-header-content">
          <div className="profile-avatar-section">
            <div className="profile-avatar-large">
              {adminProfile.fullName ? adminProfile.fullName.charAt(0).toUpperCase() : 'A'}
              <button className="avatar-edit-btn" title="Change Avatar">
                <Camera size={16} />
              </button>
            </div>
            <div className="profile-basic-info">
              <h1>{adminProfile.fullName || adminProfile.username}</h1>
              <p className="profile-email">{adminProfile.email}</p>
              <div className="profile-role-badge">
                <Shield size={14} />
                {adminProfile.role || 'Admin'}
              </div>
            </div>
          </div>
          
          <div className="profile-actions">
            {!isEditing ? (
              <button className="btn-edit" onClick={handleEditToggle}>
                <Edit3 size={16} />
                Edit Profile
              </button>
            ) : (
              <div className="edit-actions">
                <button className="btn-save" onClick={handleSaveProfile} disabled={saving}>
                  {saving ? <div className="mini-spinner"></div> : <Save size={16} />}
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
                <button className="btn-cancel" onClick={handleEditToggle}>
                  <X size={16} />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="alert alert-success">
          <CheckCircle size={16} />
          {success}
        </div>
      )}
      
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {passwordSuccess && (
        <div className="alert alert-success">
          <CheckCircle size={16} />
          {passwordSuccess}
        </div>
      )}

      <div className="admin-profile-content">
        <div className="profile-main">
          {/* Personal Information Section */}
          <div className="profile-section">
            <h3 className="section-title">
              <User size={20} />
              Personal Information
            </h3>
            
            <div className="profile-form">
              <div className="form-grid">
                <div className="form-field">
                  <label>Full Name</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="fullName"
                      value={editForm.fullName || ''}
                      onChange={handleInputChange}
                      placeholder="Enter your full name"
                    />
                  ) : (
                    <span>{adminProfile.fullName || 'Not provided'}</span>
                  )}
                </div>
                
                <div className="form-field">
                  <label>Username</label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="username"
                      value={editForm.username || ''}
                      onChange={handleInputChange}
                      placeholder="Enter username"
                    />
                  ) : (
                    <span>{adminProfile.username || 'Not provided'}</span>
                  )}
                </div>
                
                <div className="form-field">
                  <label>Email Address</label>
                  <span className="email-display">
                    <Mail size={16} />
                    {adminProfile.email}
                  </span>
                </div>
                
                <div className="form-field">
                  <label>Phone Number</label>
                  {isEditing ? (
                    <input
                      type="tel"
                      name="phone"
                      value={editForm.phone || ''}
                      onChange={handleInputChange}
                      placeholder="Enter phone number"
                    />
                  ) : (
                    <span className="phone-display">
                      <Smartphone size={16} />
                      {adminProfile.phone || 'Not provided'}
                    </span>
                  )}
                </div>
                
                <div className="form-field full-width">
                  <label>Address</label>
                  {isEditing ? (
                    <textarea
                      name="address"
                      value={editForm.address || ''}
                      onChange={handleInputChange}
                      placeholder="Enter your address"
                      rows="2"
                    />
                  ) : (
                    <span className="address-display">
                      <MapPin size={16} />
                      {adminProfile.address || 'Not provided'}
                    </span>
                  )}
                </div>
                
                <div className="form-field">
                  <label>Timezone</label>
                  {isEditing ? (
                    <select
                      name="timezone"
                      value={editForm.timezone || ''}
                      onChange={handleInputChange}
                    >
                      <option value="">Select Timezone</option>
                      <option value="UTC-05:00 (Eastern Time)">UTC-05:00 (Eastern Time)</option>
                      <option value="UTC-06:00 (Central Time)">UTC-06:00 (Central Time)</option>
                      <option value="UTC-07:00 (Mountain Time)">UTC-07:00 (Mountain Time)</option>
                      <option value="UTC-08:00 (Pacific Time)">UTC-08:00 (Pacific Time)</option>
                      <option value="UTC+00:00 (GMT)">UTC+00:00 (GMT)</option>
                      <option value="UTC+05:30 (IST)">UTC+05:30 (IST)</option>
                    </select>
                  ) : (
                    <span className="timezone-display">
                      <Globe size={16} />
                      {adminProfile.timezone || 'Not specified'}
                    </span>
                  )}
                </div>
                
                <div className="form-field">
                  <label>Language</label>
                  {isEditing ? (
                    <select
                      name="language"
                      value={editForm.language || 'en'}
                      onChange={handleInputChange}
                    >
                      <option value="en">English</option>
                      <option value="es">Español</option>
                      <option value="fr">Français</option>
                      <option value="de">Deutsch</option>
                      <option value="hi">हिन्दी</option>
                    </select>
                  ) : (
                    <span>{adminProfile.language === 'en' ? 'English' : adminProfile.language}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Security Section */}
          <div className="profile-section">
            <h3 className="section-title">
              <Lock size={20} />
              Security Settings
            </h3>
            
            <div className="security-content">
              <div className="security-item">
                <div className="security-info">
                  <h4>Password</h4>
                  <p>Last changed: {getTimeAgo(adminProfile.lastPasswordChange)}</p>
                </div>
                <button 
                  className="btn-change-password"
                  onClick={() => setShowPasswordForm(!showPasswordForm)}
                >
                  <Key size={16} />
                  Change Password
                </button>
              </div>
              
              {showPasswordForm && (
                <div className="password-form">
                  <form onSubmit={handlePasswordSubmit}>
                    <div className="password-field">
                      <label>Current Password</label>
                      <div className="password-input-group">
                        <input
                          type={showPasswords.current ? "text" : "password"}
                          name="currentPassword"
                          value={passwordForm.currentPassword}
                          onChange={handlePasswordChange}
                          placeholder="Enter current password"
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('current')}
                        >
                          {showPasswords.current ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="password-field">
                      <label>New Password</label>
                      <div className="password-input-group">
                        <input
                          type={showPasswords.new ? "text" : "password"}
                          name="newPassword"
                          value={passwordForm.newPassword}
                          onChange={handlePasswordChange}
                          placeholder="Enter new password (min 6 characters)"
                          required
                          minLength="6"
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('new')}
                        >
                          {showPasswords.new ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    
                    <div className="password-field">
                      <label>Confirm New Password</label>
                      <div className="password-input-group">
                        <input
                          type={showPasswords.confirm ? "text" : "password"}
                          name="confirmPassword"
                          value={passwordForm.confirmPassword}
                          onChange={handlePasswordChange}
                          placeholder="Confirm new password"
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => togglePasswordVisibility('confirm')}
                        >
                          {showPasswords.confirm ? <EyeOff size={16} /> : <Eye size={16} />}
                        </button>
                      </div>
                    </div>
                    
                    {passwordError && (
                      <div className="password-error">
                        <AlertCircle size={16} />
                        {passwordError}
                      </div>
                    )}
                    
                    <div className="password-actions">
                      <button type="submit" className="btn-update-password">
                        Update Password
                      </button>
                      <button 
                        type="button" 
                        className="btn-cancel-password"
                        onClick={() => {
                          setShowPasswordForm(false);
                          setPasswordForm({
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          });
                          setPasswordError('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </form>
                </div>
              )}
            </div>
          </div>

          {/* Notification Preferences */}
          <div className="profile-section">
            <h3 className="section-title">
              <Bell size={20} />
              Notification Preferences
            </h3>
            
            <div className="notification-settings">
              <div className="notification-item">
                <div className="notification-info">
                  <h4>Email Notifications</h4>
                  <p>Receive important updates via email</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="emailNotifications"
                    checked={isEditing ? editForm.emailNotifications : adminProfile.emailNotifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="notification-item">
                <div className="notification-info">
                  <h4>SMS Notifications</h4>
                  <p>Receive critical alerts via SMS</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="smsNotifications"
                    checked={isEditing ? editForm.smsNotifications : adminProfile.smsNotifications}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              
              <div className="notification-item">
                <div className="notification-info">
                  <h4>Login Alerts</h4>
                  <p>Get notified of new login attempts</p>
                </div>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    name="loginAlerts"
                    checked={isEditing ? editForm.loginAlerts : adminProfile.loginAlerts}
                    onChange={handleInputChange}
                    disabled={!isEditing}
                  />
                  <span className="toggle-slider"></span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Activity Sidebar */}
        <div className="profile-sidebar">
          <div className="profile-section">
            <h3 className="section-title">
              <Calendar size={20} />
              Account Activity
            </h3>
            
            <div className="activity-stats">
              <div className="activity-stat">
                <label>Account Created</label>
                <span>{formatDate(adminProfile.createdAt)}</span>
              </div>
              
              <div className="activity-stat">
                <label>Last Login</label>
                <span>{getTimeAgo(adminProfile.lastLogin)}</span>
              </div>
              
              <div className="activity-stat">
                <label>Total Logins</label>
                <span>{adminProfile.loginCount || 0}</span>
              </div>
              
              <div className="activity-stat">
                <label>Account Status</label>
                <span className="status-active">
                  <CheckCircle size={16} />
                  Active
                </span>
              </div>
            </div>
          </div>
          
          <div className="profile-section">
            <h3 className="section-title">Quick Actions</h3>
            <div className="quick-actions">
              <button className="quick-action-btn">
                <Shield size={16} />
                View Login History
              </button>
              <button className="quick-action-btn">
                <Bell size={16} />
                Notification Settings
              </button>
              <button className="quick-action-btn">
                <Lock size={16} />
                Security Settings
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default AdminProfile;